import { useCallback, useEffect, useState } from "react";
import {
  LiveKitRoom,
  PreJoin,
  RoomAudioRenderer,
  StartAudio,
  VideoConference
} from "@livekit/components-react";
import "@livekit/components-styles";
import { apiGet, apiPost } from "../apiClient";
import "./MeetingPage.css";

const MeetingPage = ({ token, activeMeeting, onMeetingChange }) => {
  const [meeting, setMeeting] = useState(activeMeeting || null);
  const [access, setAccess] = useState(null);
  const [title, setTitle] = useState("");
  const [choices, setChoices] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadActive = useCallback(async () => {
    try {
      const result = await apiGet("/meetings/active", { token });
      setMeeting(result);
      onMeetingChange?.(result);
    } catch (requestError) {
      setError(requestError.message || "Could not load the active meeting.");
    }
  }, [onMeetingChange, token]);

  useEffect(() => { loadActive(); }, [loadActive]);
  useEffect(() => { setMeeting(activeMeeting || null); }, [activeMeeting]);
  useEffect(() => {
    const receiveEvent = (event) => {
      const payload = event.detail;
      if (payload?.type === "meeting_started") {
        setMeeting(payload.meeting);
        onMeetingChange?.(payload.meeting);
      } else if (payload?.type === "meeting_ended" && payload.meeting?.id === meeting?.id) {
        setAccess(null);
        setMeeting(null);
        onMeetingChange?.(null);
      }
    };
    window.addEventListener("taskit:chat-event", receiveEvent);
    return () => window.removeEventListener("taskit:chat-event", receiveEvent);
  }, [meeting?.id, onMeetingChange]);

  const connect = async (deviceChoices) => {
    setLoading(true);
    setError("");
    try {
      const result = meeting
        ? await apiPost(`/meetings/${meeting.id}/join`, undefined, { token })
        : await apiPost("/meetings", { title: title.trim() || null }, { token });
      setMeeting(result);
      setChoices(deviceChoices);
      setAccess(result);
      onMeetingChange?.(result);
    } catch (requestError) {
      setError(requestError.message || "Could not join the meeting.");
    } finally {
      setLoading(false);
    }
  };

  const leave = useCallback(async () => {
    const leaving = meeting;
    setAccess(null);
    setChoices(null);
    if (leaving) {
      try { await apiPost(`/meetings/${leaving.id}/leave`, undefined, { token }); } catch { /* Local leave still succeeds. */ }
    }
    loadActive();
  }, [loadActive, meeting, token]);

  const endMeeting = async () => {
    if (!meeting || !window.confirm("End this meeting for everyone?")) return;
    try {
      await apiPost(`/meetings/${meeting.id}/end`, undefined, { token });
      setAccess(null);
      setMeeting(null);
      onMeetingChange?.(null);
    } catch (requestError) {
      setError(requestError.message || "Could not end the meeting.");
    }
  };

  if (access) {
    return (
      <section className="meeting-room" data-lk-theme="default">
        <header className="meeting-room__header">
          <div><span className="meeting-live-dot" /> Live · <strong>{meeting.title}</strong></div>
          <button className="meeting-end-button" onClick={endMeeting}>End meeting</button>
        </header>
        <LiveKitRoom
          token={access.token}
          serverUrl={access.livekit_url}
          connect
          video={choices?.videoEnabled ?? true}
          audio={choices?.audioEnabled ?? true}
          options={{ adaptiveStream: true, dynacast: true }}
          onDisconnected={leave}
          onError={(roomError) => setError(roomError.message || "Meeting connection failed.")}
        >
          <VideoConference />
          <RoomAudioRenderer />
          <StartAudio label="Enable meeting audio" />
        </LiveKitRoom>
        {error && <div className="meeting-error meeting-error--room">{error}</div>}
      </section>
    );
  }

  return (
    <section className="meeting-lobby">
      <div className="meeting-lobby__intro">
        <span className="meeting-eyebrow">Taskit Meetings</span>
        <h1>{meeting ? meeting.title : "Start a workstream meeting"}</h1>
        <p>{meeting ? "A meeting is live now. Check your devices and join your teammates." : "Meet face-to-face without leaving your workstream."}</p>
        {!meeting && (
          <label className="meeting-title-field">
            Meeting title
            <input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Weekly project sync" maxLength={255} />
          </label>
        )}
      </div>
      <div className="meeting-prejoin" data-lk-theme="default">
        <PreJoin
          onSubmit={connect}
          joinLabel={loading ? "Connecting…" : meeting ? "Join meeting" : "Start meeting"}
          persistUserChoices
          userLabel="Your display name comes from your Taskit account"
        />
        {error && <div className="meeting-error">{error}</div>}
      </div>
    </section>
  );
};

export default MeetingPage;
